/** 7 -> 8: two fields nothing ever read are dropped.
 *
 * `turn.weekEvent` was written at the start of every week and read by no rule,
 * no screen and no AI: the week's name reaches everyone through the
 * `WeekStarted` / `MonthStarted` events instead, and the field was never even
 * reset when the week ended. `battle.siege.towerHp` carried a hit-point budget
 * per turret (20 for the keep, 12 for a flank) that the battle kernel never
 * consulted - a turret is an `Obstacle` and falls on its `integrity` like a
 * wall segment - so the numbers were a promise the fight did not keep.
 *
 * `shapes.ts` closes every key set, so a save carrying either field would be
 * refused by the validator after this rung rather than decoded with a stray
 * key; the rung removes them. Nothing is added and no value is derived, so the
 * state a lifted file describes is the state it always described.
 */

import type { Migration } from "./types.js";
import { isRecord, readSave, writeSave } from "./tree.js";

function without(t: Record<string, unknown>, key: string): Record<string, unknown> {
  if (!Object.prototype.hasOwnProperty.call(t, key)) return t;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(t).sort()) if (k !== key) out[k] = t[k];
  return out;
}

export const migration0008: Migration = {
  from: 7,
  to: 8,
  describe: "drop turn.weekEvent and battle.siege.towerHp, which nothing read",
  migrate(raw: unknown): Record<string, unknown> | null {
    const save = readSave(raw);
    if (save === null) return null;

    let state: Record<string, unknown> = save.state;
    const turn = state["turn"];
    if (isRecord(turn)) state = { ...state, turn: without(turn, "weekEvent") };

    const battle = state["battle"];
    if (isRecord(battle)) {
      const siege = battle["siege"];
      if (isRecord(siege)) {
        state = { ...state, battle: { ...battle, siege: without(siege, "towerHp") } };
      }
    }
    return writeSave(save, state, 8);
  },
};
